import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FaPlus, FaWhatsapp } from 'react-icons/fa';
import {
  FiChevronDown,
  FiChevronUp,
  FiShoppingCart,
  FiTag,
  FiTruck,
  FiX,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import Header from '../components/header/Header.tsx';
import ConfirmModal from '../components/modal/ConfirmModal.tsx';
import { fetchActiveDiscount } from '../discount/discountService.ts';
import { Discount } from '../discount/discountTypes.ts';
import { useLoadingContext } from '../login/context/LoadingContext.tsx';
import CartItemList from '../order/cartItem/CartItemList.tsx';
import {
  initialShoppingCart,
  useProductContext,
} from '../product/context/ProductContext.tsx';
import { Product } from '../product/ProductType.ts';
import { useUserContext } from '../user/context/UserContext.tsx';
import UserInfoForm from '../user/UserInfoForm.tsx';
import {
  HOST,
  minimumPurchaseAmount,
  PaymentMethod,
} from '../utils/constants.ts';
import { formatPriceWithDots, getUnitPrice } from '../utils/utils.ts';
import { ConfirmOrderModalBody } from './ConfirmOrderModalBody.tsx';
import './shopping-cart.scss';

const ShoppingCart = () => {
  const { shoppingCart, deleteFromCart, setShoppingCart } = useProductContext();
  const { setUserInfo } = useUserContext();
  const { userInfo } = useUserContext();
  const { showLoading, hideLoading } = useLoadingContext();
  const navigate = useNavigate();
  const [isOpenConfirmModal, setIsOpenConfirmModal] = useState<boolean>(false);
  const [modalBodyText, setModalBodyText] = useState<React.ReactNode>();
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.CASH);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [infoCollapsed, setInfoCollapsed] = useState<{
    [key: string]: boolean;
  }>({ envios: true, cancelar: true, descuento: true });
  const [activeDiscount, setActiveDiscount] = useState<Discount | null>(null);
  const toggleInfo = (id: string) =>
    setInfoCollapsed((prev) => ({ ...prev, [id]: !prev[id] }));
  const hasShownUpdateToast = useRef(false);
  const topRef = useRef<HTMLDivElement>(null);
  const scheduleRef = useRef<HTMLDivElement>(null);
  const scrollToErrorRef = useRef(false);

  useEffect(() => {
    if (scrollToErrorRef.current && invalidFields.length > 0) {
      scrollToErrorRef.current = false;
      const el = document.querySelector<HTMLElement>(
        `[data-field="${invalidFields[0]}"]`
      );
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [invalidFields]);

  const userData = useMemo(() => {
    return (
      userInfo?.user || JSON.parse(localStorage.getItem('userInfo') || '{}')
    );
  }, [userInfo]);

  const [initialUserData] = useState({
    name: userData.name || '',
    phone: userData.phone || '',
    address: userData.address || '',
    city: userData.city || '',
    timeSlot: userData.timeSlot || '',
  });

  const [userDataForm, setUserDataForm] = useState({
    name: userData.name || '',
    phone: userData.phone || '',
    address: userData.address || '',
    city: userData.city || '',
    description: userData.description || '',
    email: userData.email || '',
    timeSlot: userData.timeSlot || '',
  });

  useEffect(() => {
    if (!userInfo) {
      navigate('/login');
    }
  }, [userInfo, navigate]);

  useEffect(() => {
    const updateCartPrices = async () => {
      try {
        const response = await fetch(`${HOST}/products`);
        if (!response.ok) throw new Error('Error al obtener productos');

        const backendProducts = await response.json();

        let updated = false;
        let hasOutOfStock = false;
        const outOfStockProducts: string[] = [];

        const updatedItems = shoppingCart.items
          .map((item) => {
            const latestProduct: Product = backendProducts.find(
              (p: Product) => p.id === item.product.id
            );
            if (!latestProduct) return item;

            // Verificar si el producto ya no tiene stock
            if (latestProduct.hasStock === false) {
              hasOutOfStock = true;
              outOfStockProducts.push(latestProduct.name);
              return null; // Marcar para remover
            }

            const correctUnitPrice = getUnitPrice(
              latestProduct.price,
              item.purchaseOption,
              latestProduct.productType,
              latestProduct.specialPrices
            );

            if (item.partialPrice !== correctUnitPrice) {
              updated = true;
              return {
                ...item,
                partialPrice: correctUnitPrice,
                product: { ...item.product, price: latestProduct.price },
              };
            }

            return item;
          })
          .filter((item) => item !== null); // Remover items sin stock

        if ((updated || hasOutOfStock) && !hasShownUpdateToast.current) {
          const updatedTotal = updatedItems.reduce(
            (acc, item) => acc + item.partialPrice * item.quantity,
            0
          );
          const updatedTotalItems = updatedItems.reduce(
            (acc, item) => acc + item.quantity,
            0
          );

          setShoppingCart({
            items: updatedItems,
            totalPrice: updatedTotal,
            totalItems: updatedTotalItems,
          });

          if (hasOutOfStock) {
            toast.error(
              `⚠️ Los siguientes productos fueron removidos del carrito porque ya no tienen stock: ${outOfStockProducts.join(', ')}`,
              { duration: 6000 }
            );
          } else if (updated) {
            toast('🛒 Algunos precios en tu carrito fueron actualizados');
          }
          hasShownUpdateToast.current = true;
        }
      } catch (error) {
        console.error('Error al actualizar precios del carrito:', error);
      }
    };

    updateCartPrices();
  }, [setShoppingCart, shoppingCart.items]);

  // Fetch active discount when payment method changes
  useEffect(() => {
    let cancelled = false;
    fetchActiveDiscount(paymentMethod).then((d) => {
      if (!cancelled) setActiveDiscount(d);
    });
    return () => {
      cancelled = true;
    };
  }, [paymentMethod]);

  const shippingCost = 0; // Envío gratis por comprar por la web

  // Payment-method promo discount from backend
  const promoDiscountAmount = activeDiscount
    ? Number(
        (
          (shoppingCart.totalPrice * activeDiscount.discountPercentage) /
          100
        ).toFixed(2)
      )
    : 0;

  // First purchase 10% discount
  const discountAmount = userInfo?.user?.firstPurchase
    ? Number((shoppingCart.totalPrice * 0.1).toFixed(2))
    : 0;

  const finalDiscount = discountAmount + promoDiscountAmount;

  const totalWithShipping =
    shoppingCart.totalPrice - finalDiscount + shippingCost;

  const savedToken = localStorage.getItem('token');

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setUserDataForm((prev) => ({ ...prev, [name]: value }));
    setInvalidFields((prev) => prev.filter((f) => f !== name));
  };

  const validateOrder = () => {
    if (shoppingCart.totalPrice < minimumPurchaseAmount) {
      toast.error(
        `El monto mínimo para realizar un pedido es de $${minimumPurchaseAmount.toLocaleString('es-AR')}`
      );
      return false;
    }

    const fieldLabels: Record<string, string> = {
      name: 'nombre',
      phone: 'teléfono',
      address: 'dirección',
      city: 'ciudad',
      timeSlot: 'horario de entrega',
    };

    const missingFields: string[] = [];
    if (!userDataForm.name?.trim()) missingFields.push('name');
    if (!userDataForm.phone?.trim()) missingFields.push('phone');
    if (!userDataForm.address?.trim()) missingFields.push('address');
    if (!userDataForm.city?.trim()) missingFields.push('city');
    if (!userDataForm.timeSlot) missingFields.push('timeSlot');

    if (missingFields.length > 0) {
      const labels = missingFields.map((f) => fieldLabels[f]).join(', ');
      toast.error(`Por favor, completa los siguientes campos: ${labels}`, {
        duration: 4000,
      });
      scrollToErrorRef.current = true;
      setInvalidFields(missingFields);
      return false;
    }

    return true;
  };

  const handleFinishOrder = () => {
    if (validateOrder()) {
      setModalBodyText(ConfirmOrderModalBody(paymentMethod));
      setIsOpenConfirmModal(true);
    }
  };

  const finishOrder = async () => {
    // Validar stock de todos los productos antes de crear la orden
    try {
      const response = await fetch(`${HOST}/products`);
      if (!response.ok) throw new Error('Error al validar stock');

      const backendProducts = await response.json();
      const outOfStockItems: string[] = [];

      shoppingCart.items.forEach((item) => {
        const product = backendProducts.find(
          (p: Product) => p.id === item.product.id
        );
        if (product && product.hasStock === false) {
          outOfStockItems.push(product.name);
        }
      });

      if (outOfStockItems.length > 0) {
        toast.error(
          `No se puede completar la orden. Los siguientes productos ya no tienen stock: ${outOfStockItems.join(', ')}. Por favor, actualiza tu carrito.`,
          { duration: 6000 }
        );
        // Forzar actualización del carrito
        window.location.reload();
        return;
      }
    } catch (error) {
      console.error('Error validando stock:', error);
      toast.error('Error al validar disponibilidad de productos');
      return;
    }

    const sanitizedCart = {
      ...shoppingCart,
      items: shoppingCart.items.map((item) => {
        const updatedItem = {
          ...item,
          product: { ...item.product, image: undefined },
          totalPrice: item.partialPrice * item.quantity,
        };
        return updatedItem;
      }),
    };

    const orderData: Record<string, any> = {
      userId: userInfo?.user.id,
      cartData: sanitizedCart,
      timeSlot: userDataForm.timeSlot,
      description: userDataForm.description,
      paymentMethod,
      totalPrice: totalWithShipping,
    };

    if (activeDiscount) {
      orderData.discountId = activeDiscount.id;
      orderData.originalPrice = shoppingCart.totalPrice;
    }

    try {
      showLoading();

      // Actualizar info del usuario ANTES de crear la orden,
      // para que el backend tenga dirección/teléfono al generar el email
      const changedFields: Record<string, any> = {};

      if (userDataForm.name !== initialUserData.name) {
        changedFields.name = userDataForm.name;
      }
      if (userDataForm.phone !== initialUserData.phone) {
        changedFields.phone = userDataForm.phone;
      }
      if (userDataForm.address !== initialUserData.address) {
        changedFields.address = userDataForm.address;
      }
      if (userDataForm.city !== initialUserData.city) {
        changedFields.city = userDataForm.city;
      }
      if (userDataForm.timeSlot !== initialUserData.timeSlot) {
        changedFields.timeSlot = userDataForm.timeSlot;
      }

      if (Object.keys(changedFields).length > 0) {
        if (!userInfo || !savedToken) {
          throw new Error('Usuario no autenticado');
        }

        const updateResponse = await fetch(
          `${HOST}/users/${userInfo.user.id}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${savedToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(changedFields),
          }
        );

        if (updateResponse.ok) {
          const updatedUser = await updateResponse.json();
          const updatedUserInfo = {
            user: {
              ...userInfo.user,
              ...updatedUser,
            },
            token: savedToken,
          };
          setUserInfo(updatedUserInfo);
          localStorage.setItem('userInfo', JSON.stringify(updatedUser));
        }
      }

      // Crear la orden (el backend ya tiene la info actualizada del usuario)
      const response = await fetch(`${HOST}/orders`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${savedToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) throw new Error('Error al realizar el pedido');

      if (userInfo?.user?.firstPurchase) {
        try {
          await fetch(`${HOST}/users/${userInfo.user.id}`, {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${savedToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ firstPurchase: false }),
          });

          const updatedUserInfo = {
            ...userInfo,
            user: {
              ...userInfo.user,
              firstPurchase: false,
            },
          };
          setUserInfo(updatedUserInfo);
          localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
        } catch (err) {
          console.error('Error actualizando firstPurchase:', err);
        }
      }

      localStorage.removeItem('shoppingCart');
      setShoppingCart(initialShoppingCart);
      navigate('/pedido-confirmado?status=approved');
    } catch (error) {
      console.error(error);
      toast.error('Ocurrió un error al procesar el pedido');
    } finally {
      hideLoading();
      setIsOpenConfirmModal(false);
    }
  };

  return (
    <div className="shopping-cart-container">
      <>
        {isOpenConfirmModal && (
          <ConfirmModal
            confirmText="Finalizar pedido"
            bodyText={modalBodyText}
            action={finishOrder}
            cancel={() => setIsOpenConfirmModal(false)}
          />
        )}
        <Header />
        <div ref={topRef} />
        <div>
          <h2 className="welcome-title">Carrito de compras</h2>
          <aside className="margin-top-8">
            <h4>¡Envío GRATIS por comprar en la web! 🎉</h4>
            <div className="shopping-info-container margin-top-8">
              <div className="flex-align-center flex-direction-column align-items-flex-start">
                <h4>
                  Compra mínima:{' '}
                  <span className="highlight-amount">
                    ${formatPriceWithDots(minimumPurchaseAmount)}
                  </span>
                </h4>

                {shoppingCart.totalPrice < minimumPurchaseAmount && (
                  <span>
                    {`Solo te faltan $${(
                      minimumPurchaseAmount - shoppingCart.totalPrice
                    ).toLocaleString('es-AR')} para poder realizar tu pedido`}
                  </span>
                )}
              </div>
              <div className="flex-align-center">
                <button
                  className="primary-button align-icon-text"
                  onClick={() => navigate('/')}
                >
                  <FaPlus className="icon-responsive" /> Agregar productos
                </button>
              </div>
            </div>
          </aside>

          {shoppingCart.totalItems === 0 ? (
            <div className="empty-cart">
              <FiShoppingCart className="empty-cart__icon" />
              <h3 className="empty-cart__title">Tu carrito está vacío</h3>
              <p className="empty-cart__subtitle">
                ¡Agregá frutas y verduras frescas y aprovechá el envío gratis!
              </p>
              <button
                className="primary-button empty-cart__cta"
                onClick={() => navigate('/')}
              >
                Explorar productos
              </button>
            </div>
          ) : (
            <>
              <CartItemList
                items={shoppingCart.items}
                showDeleteButton={true}
                deleteFromCart={deleteFromCart}
              />
              <div className="margin-top margin-top-8 order-summary">
                <div className="summary-row">
                  <span className="summary-label">Total del pedido:</span>
                  <span className="summary-value">
                    ${formatPriceWithDots(shoppingCart.totalPrice)}
                  </span>
                </div>

                <div className="summary-row">
                  <span className="summary-label">Envío:</span>
                  <span className="summary-value">
                    {shippingCost === 0
                      ? 'Gratis'
                      : `$${formatPriceWithDots(shippingCost)}`}
                  </span>
                </div>

                {discountAmount > 0 && (
                  <div className="summary-row">
                    <span className="summary-label">
                      Descuento 10% primera compra:
                    </span>
                    <span className="summary-value">
                      -${formatPriceWithDots(discountAmount)}
                    </span>
                  </div>
                )}

                {promoDiscountAmount > 0 && activeDiscount && (
                  <div className="summary-row summary-row--promo">
                    <span className="summary-label">
                      🎉 {activeDiscount.discountPercentage}% desc. (
                      {activeDiscount.name}):
                    </span>
                    <span className="summary-value">
                      -${formatPriceWithDots(promoDiscountAmount)}
                    </span>
                  </div>
                )}

                <div className="summary-row">
                  <span className="summary-label">Precio Total:</span>
                  <span className="summary-value">
                    ${formatPriceWithDots(totalWithShipping)}
                  </span>
                </div>
              </div>
            </>
          )}

          {shoppingCart.totalPrice >= minimumPurchaseAmount && (
            <>
              <UserInfoForm
                userInfo={userDataForm}
                handleInputChange={handleInputChange}
                scheduleRef={scheduleRef}
                invalidFields={invalidFields}
                paymentSection={
                  <>
                    <div className="payment-method-container">
                      <label className="label">Medio de pago:</label>
                      <div className="payment-method-options">
                        <label>
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={PaymentMethod.CASH}
                            checked={paymentMethod === PaymentMethod.CASH}
                            onChange={() =>
                              setPaymentMethod(PaymentMethod.CASH)
                            }
                          />{' '}
                          Efectivo
                        </label>
                        {/* <label>
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={PaymentMethod.MERCADOPAGO}
                          checked={paymentMethod === PaymentMethod.MERCADOPAGO}
                          onChange={() =>
                            setPaymentMethod(PaymentMethod.MERCADOPAGO)
                          }
                        />{' '}
                        Mercado Pago
                      </label> */}
                        <label>
                          <input
                            type="radio"
                            name="paymentMethod"
                            value={PaymentMethod.TRANSFER}
                            checked={paymentMethod === PaymentMethod.TRANSFER}
                            onChange={() =>
                              setPaymentMethod(PaymentMethod.TRANSFER)
                            }
                          />{' '}
                          Transferencia
                        </label>
                      </div>
                    </div>
                    <div>
                      <label>
                        Alias: <span className="alias">rapifruti</span>{' '}
                      </label>
                    </div>
                    {activeDiscount && (
                      <div className="promo-banner">
                        🎉 {activeDiscount.discountPercentage}% de descuento
                        pagando con{' '}
                        {activeDiscount.paymentMethods
                          .map((m) =>
                            m === 'efectivo'
                              ? 'efectivo'
                              : m === 'transferencia'
                                ? 'transferencia'
                                : 'Mercado Pago'
                          )
                          .join(' / ')}
                      </div>
                    )}
                  </>
                }
              />
            </>
          )}

          <div className="info-group">
            <div
              className="info-group__row"
              onClick={() => toggleInfo('envios')}
            >
              <span className="info-card__icon">
                <FiTruck size={18} />
              </span>
              <span className="info-card__label">Horarios de envío</span>
              {infoCollapsed['envios'] ? (
                <FiChevronDown size={15} />
              ) : (
                <FiChevronUp size={15} />
              )}
            </div>
            {!infoCollapsed['envios'] && (
              <div className="info-group__body">
                <span className="info-card__text">
                  Los envíos se realizan de lunes a sábado el mismo día, siempre
                  que el pedido se haga antes de las 7:30 a.m. Los pedidos
                  realizados después de ese horario se entregarán al día
                  siguiente.
                </span>
              </div>
            )}
            <div className="info-group__divider" />
            <div
              className="info-group__row"
              onClick={() => toggleInfo('cancelar')}
            >
              <span className="info-card__icon">
                <FiX size={18} />
              </span>
              <span className="info-card__label">Cancelación de envío</span>
              {infoCollapsed['cancelar'] ? (
                <FiChevronDown size={15} />
              ) : (
                <FiChevronUp size={15} />
              )}
            </div>
            {!infoCollapsed['cancelar'] && (
              <div className="info-group__body">
                <span className="info-card__text">
                  En caso de querer cancelar un envío comunicarse via whatsapp.
                </span>
              </div>
            )}
            {userInfo?.user?.firstPurchase && (
              <>
                <div className="info-group__divider" />
                <div
                  className="info-group__row"
                  onClick={() => toggleInfo('descuento')}
                >
                  <span
                    className="info-card__icon"
                    style={{ color: '#2e7d32' }}
                  >
                    <FiTag size={18} />
                  </span>
                  <span className="info-card__label">
                    🎉 Descuento primera compra
                  </span>
                  {infoCollapsed['descuento'] ? (
                    <FiChevronDown size={15} />
                  ) : (
                    <FiChevronUp size={15} />
                  )}
                </div>
                {!infoCollapsed['descuento'] && (
                  <div className="info-group__body">
                    <span className="info-card__text">
                      Por ser tu primera compra tenés un 10% de descuento total
                      en tu pedido.
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <button
            className="primary-button margin-top-40 font-18 margin-bottom-20 realizar-pedido-desktop-btn"
            disabled={shoppingCart.totalPrice < minimumPurchaseAmount}
            onClick={handleFinishOrder}
          >
            Realizar Pedido
          </button>
        </div>
      </>
      <div className="realizar-pedido-float">
        <button
          className="primary-button font-18"
          disabled={shoppingCart.totalPrice < minimumPurchaseAmount}
          onClick={handleFinishOrder}
        >
          Realizar Pedido
        </button>
        <button
          className="whatsapp-float-btn"
          onClick={() =>
            window.open(
              'https://wa.me/541124912757',
              '_blank',
              'noopener,noreferrer'
            )
          }
          aria-label="WhatsApp"
        >
          <FaWhatsapp size={24} />
        </button>
      </div>
    </div>
  );
};

export default ShoppingCart;
